import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useNavigate } from "react-router-dom"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { MoreHorizontal, FileText, Archive, Trash2, Copy, ExternalLink } from "lucide-react"
import { motion } from "motion/react"
import { Avatar } from "@/components/ui/Avatar"
import { PreviewCard, PreviewCardTrigger, PreviewCardPopup } from "@/components/ui/preview-card"
import { Menu, MenuTrigger, MenuPopup, MenuItem } from "@/components/ui/menu"
import { cn } from "@/utils/Functions"
import { useDemoUsers } from "@/hooks/common/useDemoUsers"
import type { DemoUserId } from "@/types/demo.types"
import { notifyFeature } from "@/utils/demoNotifications"
import type { Deal } from "@/types/deal.types"
import { DEAL_TYPE_CONFIG } from "@/constants/deal.constants"
import { formatCurrency } from "@/views/deals/utils"

interface DealKanbanCardProps {
  deal: Deal
  isDragging?: boolean
}

export function DealKanbanCard({ deal, isDragging }: DealKanbanCardProps) {
  const navigate = useNavigate()
  const isDemo = useIsDemo()
  const { profilePictures: demoProfilePictures, userDisplayInfo: demoUserDisplayInfo } = useDemoUsers()

  const { attributes, listeners, setNodeRef, transform, transition, isDragging: isSortableDragging } = useSortable({
    id: deal.id,
  })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    if ((e.target as HTMLElement).closest("[data-menu]")) return
    navigate(`${isDemo ? "/demo" : ""}/deal/${deal.id}`)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handleClick(e)
    }
  }

  const handleAction = (action: string) => {
    notifyFeature(action, true)
  }

  const dealTypeConfig = DEAL_TYPE_CONFIG[deal.dealType]
  const actualIsDragging = isDragging || isSortableDragging

  return (
    <motion.div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View deal: ${deal.name}`}
      className={cn(
        "group relative bg-neutral-100 dark:bg-neutral-800 border border-border rounded-xl p-3 cursor-pointer",
        "hover:border-border/80 hover:shadow-sm",
        "transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        actualIsDragging && "cursor-grabbing opacity-50 shadow-lg scale-[1.02] rotate-1",
      )}
    >
      <div>
        <div className="flex items-start justify-between gap-2 mb-2">
          <span className={cn("text-xs px-2 py-0.5 rounded-full font-medium", dealTypeConfig.color)}>Deal</span>

          <Menu>
            <MenuTrigger
              data-menu
              onClick={(e) => e.stopPropagation()}
              aria-label="Deal options"
              className="p-1 rounded-md text-muted-foreground/50 hover:text-muted-foreground opacity-0 group-hover:opacity-100"
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </MenuTrigger>
            <MenuPopup align="end" sideOffset={4} className="min-w-[140px]">
              <MenuItem onClick={() => handleAction("Open Deal")}>
                <ExternalLink className="size-4" aria-hidden="true" />
                Open
              </MenuItem>
              <MenuItem onClick={() => handleAction("Duplicate Deal")}>
                <Copy className="size-4" aria-hidden="true" />
                Duplicate
              </MenuItem>
              <MenuItem onClick={() => handleAction("Archive Deal")}>
                <Archive className="size-4" aria-hidden="true" />
                Archive
              </MenuItem>
              <MenuItem onClick={() => handleAction("Delete Deal")} className="text-destructive-foreground">
                <Trash2 className="size-4" aria-hidden="true" />
                Delete
              </MenuItem>
            </MenuPopup>
          </Menu>
        </div>

        <h4 className="text-sm font-medium text-foreground line-clamp-2 mb-1">{deal.name}</h4>
        <p className="text-xs text-muted-foreground mb-3">{deal.type}</p>
        <div className="text-base font-semibold text-foreground mb-3">{formatCurrency(deal.value)}</div>

        <div className="flex items-center justify-between pt-2 border-t border-border/50">
          <div className="flex items-center gap-1 text-muted-foreground/60 text-xs">
            <FileText className="size-3" aria-hidden="true" />
            <span>{deal.documentsCount}</span>
          </div>

          {deal.collaborators.length > 0 && (
            <div className="flex -space-x-1.5">
              {deal.collaborators.slice(0, 3).map((odId) => {
                const userId = odId as DemoUserId
                const userInfo = demoUserDisplayInfo[userId]

                return (
                  <PreviewCard key={userId}>
                    <PreviewCardTrigger
                      onClick={(e) => e.stopPropagation()}
                      className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Avatar src={demoProfilePictures[userId]} className="size-5 border border-card" />
                    </PreviewCardTrigger>
                    <PreviewCardPopup sideOffset={8}>
                      <div className="flex gap-3">
                        <Avatar src={demoProfilePictures[userId]} className="size-10 shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground truncate">{userInfo?.displayName ?? "Unknown"}</p>
                          <p className="text-xs text-muted-foreground truncate">{userInfo?.email ?? ""}</p>
                        </div>
                      </div>
                    </PreviewCardPopup>
                  </PreviewCard>
                )
              })}
              {deal.collaborators.length > 3 && (
                <div
                  className="size-5 rounded-full bg-muted border border-card flex items-center justify-center text-[10px] text-muted-foreground"
                  aria-label={`${deal.collaborators.length - 3} additional collaborators`}
                >
                  +{deal.collaborators.length - 3}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  )
}
